import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Search, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { captureError } from '@/lib/logger';
import { formatBusinessDate, formatMoney } from '@/lib/format';
import { buildAllocationRows, BOE_LINK_COLUMNS, type AllocationMember, type AllocationRow } from './customsAllocation';
import type { ContainerBoe } from './ContainerCustomsSheet';
import type { ContainerMember } from './ProcurementContainers';

// ─── Types ──────────────────────────────────────────────────────────────────

type PaymentType = 'tax_bill' | 'port_charges' | 'levy';

interface MemberTransactionRow {
    vehicle_id: number;
    transaction_type: PaymentType;
    allocated_amount: number | null;
    allocated_fee: number | null;
    transaction_id: number;
}

interface UnassignedTransactionSearchResult {
    id: number;
    amount: number;
    currency: string;
    transaction_at: string;
    description: string | null;
}

interface TransactionDetail {
    id: number;
    amount: number;
    fee: number | null;
    currency: string;
    transaction_at: string;
    description: string | null;
}

interface CustomsPaymentsPanelProps {
    memberIds: number[];
    fullMembers: ContainerMember[];
    boe: ContainerBoe;
}

const TYPE_META: Record<PaymentType, { label: string }> = {
    tax_bill: { label: 'Tax Bill' },
    port_charges: { label: 'Port Charges' },
    levy: { label: 'Customs House Agent Association Membership Levy' },
};

// ─── Shared data — existing tax_bill/port_charges/levy links for this container's members ─────

function useMemberTransactionsByType(memberIds: number[]) {
    return useQuery({
        queryKey: ['container-member-transactions', memberIds],
        queryFn: async (): Promise<MemberTransactionRow[]> => {
            if (memberIds.length === 0) return [];
            const { data, error } = await supabase
                .from('vehicle_transactions')
                .select('vehicle_id, transaction_type, allocated_amount, allocated_fee, transaction_id')
                .in('vehicle_id', memberIds)
                .in('transaction_type', ['tax_bill', 'port_charges', 'levy']);
            if (error) throw error;
            return (data ?? []) as unknown as MemberTransactionRow[];
        },
        enabled: memberIds.length > 0,
    });
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function CustomsPaymentsPanel({ memberIds, fullMembers, boe }: CustomsPaymentsPanelProps) {
    const { data: memberTransactions } = useMemberTransactionsByType(memberIds);

    const boeAmountsByVehicleId = useMemo(() => {
        const map = new Map<number, number>();
        boe.items.forEach(item => {
            if (item.vehicle_id != null) map.set(item.vehicle_id, item.tax_amount);
        });
        return map;
    }, [boe.items]);

    return (
        <div className="space-y-6 pb-6">
            <PaymentTypeCard
                type="tax_bill"
                boe={boe}
                fullMembers={fullMembers}
                memberTransactions={memberTransactions ?? []}
                boeAmountsByVehicleId={boeAmountsByVehicleId}
            />
            <PaymentTypeCard
                type="levy"
                boe={boe}
                fullMembers={fullMembers}
                memberTransactions={memberTransactions ?? []}
                boeAmountsByVehicleId={null}
            />
            <PaymentTypeCard
                type="port_charges"
                boe={boe}
                fullMembers={fullMembers}
                memberTransactions={memberTransactions ?? []}
                boeAmountsByVehicleId={null}
            />
        </div>
    );
}

// ─── Type card (Tax Bill, Port Charges, or Levy) ───────────────────────────

/** BOE embed field holding each type's linked transaction summary — the `*_transaction` counterpart
 * to `BOE_LINK_COLUMNS`' `*_transaction_id` link column, keyed the same way. */
const BOE_SUMMARY_KEYS: Record<PaymentType, 'tax_transaction' | 'port_charges_transaction' | 'levy_transaction'> = {
    tax_bill: 'tax_transaction',
    port_charges: 'port_charges_transaction',
    levy: 'levy_transaction',
};

function PaymentTypeCard({ type, boe, fullMembers, memberTransactions, boeAmountsByVehicleId }: {
    type: PaymentType;
    boe: ContainerBoe;
    fullMembers: ContainerMember[];
    memberTransactions: MemberTransactionRow[];
    /** BOE-assessed per-vehicle amount — tax_bill only; port_charges and levy have no per-vehicle
     * BOE figure (always even split), so callers pass null. */
    boeAmountsByVehicleId: Map<number, number> | null;
}) {
    const queryClient = useQueryClient();
    const meta = TYPE_META[type];
    const linkedTransactionId = boe[BOE_LINK_COLUMNS[type]];
    const linkedTransactionSummary = boe[BOE_SUMMARY_KEYS[type]];

    const vtByVehicleId = useMemo(() => {
        const map = new Map<number, MemberTransactionRow>();
        memberTransactions.filter(r => r.transaction_type === type).forEach(r => map.set(r.vehicle_id, r));
        return map;
    }, [memberTransactions, type]);

    const fullMembersByVehicleId = useMemo(() => {
        const map = new Map<number, ContainerMember>();
        fullMembers.forEach(m => map.set(m.id, m));
        return map;
    }, [fullMembers]);

    // Ground truth for "who's in this payment split" is the BOE's own items, not the container
    // tile's fullMembers snapshot — that snapshot can go stale once vehicles are added/removed from
    // the container after the BOE was recorded (see ContainerCustomsSheet's useContainerBoe).
    // fullMembers is used only as a label (make/model) lookup fallback below.
    const allMembers: AllocationMember[] = useMemo(() => {
        const boeVehicleItems = boe.items.filter(item => item.vehicle_id != null);
        if (boeVehicleItems.length === 0) {
            // Defensive fallback — practically unreachable (the Payments tab is disabled until a
            // BOE with items exists), but avoids an empty allocation table if it ever does happen.
            return fullMembers.map(m => ({
                vehicleId: m.id,
                vin: m.vin,
                label: `${m.make} ${m.model}`,
                existingTransactionId: vtByVehicleId.get(m.id)?.transaction_id ?? null,
            }));
        }
        return boeVehicleItems.map(item => {
            const vehicleId = item.vehicle_id as number;
            const member = fullMembersByVehicleId.get(vehicleId);
            return {
                vehicleId,
                vin: item.vin,
                label: member ? `${member.make} ${member.model}` : item.vin,
                existingTransactionId: vtByVehicleId.get(vehicleId)?.transaction_id ?? null,
            };
        });
    }, [boe.items, fullMembers, fullMembersByVehicleId, vtByVehicleId]);

    const eligibleMembers = useMemo(() => allMembers.filter(m => m.existingTransactionId === null), [allMembers]);
    const alreadyAssignedMembers = useMemo(() => allMembers.filter(m => m.existingTransactionId !== null), [allMembers]);

    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, 300);
    const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
    const [rows, setRows] = useState<AllocationRow[]>([]);
    const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);

    const { data: searchResults, isLoading: searchLoading } = useQuery({
        queryKey: ['unassigned-debit-transactions', type, debouncedSearch],
        queryFn: async (): Promise<UnassignedTransactionSearchResult[]> => {
            const { data, error } = await supabase.rpc('get_unassigned_transactions', {
                search_query: debouncedSearch || null,
                limit_count: 20,
                type_filter: 'DEBIT',
            });
            if (error) throw error;
            return (data ?? []) as UnassignedTransactionSearchResult[];
        },
        enabled: !linkedTransactionId,
    });

    // get_unassigned_transactions doesn't return `fee` (search-result shape only) — fetch the full
    // transaction row once selected so the fee can be split/reconciled too.
    const { data: selectedTransaction } = useQuery({
        queryKey: ['transaction-detail', selectedTransactionId],
        queryFn: async (): Promise<TransactionDetail> => {
            const { data, error } = await supabase
                .from('transactions')
                .select('id, amount, fee, currency, transaction_at, description')
                .eq('id', selectedTransactionId as number)
                .single();
            if (error) throw error;
            return data as TransactionDetail;
        },
        enabled: selectedTransactionId != null,
    });

    useEffect(() => {
        if (selectedTransaction) {
            setRows(buildAllocationRows(eligibleMembers, selectedTransaction.amount, selectedTransaction.fee ?? 0, boeAmountsByVehicleId));
        } else {
            setRows([]);
        }
        // Intentionally keyed only on the selected transaction — eligibleMembers/boeAmountsByVehicleId
        // are recomputed fresh each render from stable container data; re-running the prefill on every
        // render would clobber in-progress row edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTransaction?.id]);

    const invalidateAfterAssignmentChange = () => {
        queryClient.invalidateQueries({ queryKey: ['procurement-containers-vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
        queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });
        queryClient.invalidateQueries({ queryKey: ['container-boe'] });
        queryClient.invalidateQueries({ queryKey: ['all-vehicle-transactions'] });
        queryClient.invalidateQueries({ queryKey: ['vehicle-transaction-stats'] });
        queryClient.invalidateQueries({ queryKey: ['container-member-transactions'] });
        queryClient.invalidateQueries({ queryKey: ['vehicle-financials'] });
        // Not in the plan's explicit list, but needed: once assigned, the transaction must drop out of
        // BOTH cards' unassigned-search caches (a transaction can't be validly picked twice).
        queryClient.invalidateQueries({ queryKey: ['unassigned-debit-transactions'] });
    };

    const confirmMutation = useMutation({
        mutationFn: async () => {
            if (!selectedTransaction || rows.length === 0) throw new Error('Nothing to assign');

            // Link column FIRST — if the insert below fails partway, the BOE is already linked to
            // this transaction, so the card renders as Assigned with a working Unassign that
            // self-heals, instead of a dead state with no link and no way to retry from the UI.
            const { error: boeErr } = await supabase
                .from('bills_of_entry')
                .update({ [BOE_LINK_COLUMNS[type]]: selectedTransaction.id })
                .eq('id', boe.id);
            if (boeErr) throw boeErr;

            const inserts = rows.map(r => ({
                vehicle_id: r.vehicleId,
                transaction_id: selectedTransaction.id,
                transaction_type: type,
                allocated_amount: r.amount,
                allocated_fee: r.fee,
            }));
            // Upsert (not insert) — idempotent-safe so a retry after the link column succeeded but
            // this insert only partially completed doesn't error on the rows that already made it in.
            const { error: insErr } = await supabase
                .from('vehicle_transactions')
                .upsert(inserts, { onConflict: 'transaction_id,vehicle_id,transaction_type', ignoreDuplicates: true });
            if (insErr) throw insErr;
        },
        onSuccess: () => {
            toast.success(`${meta.label} assigned to ${rows.length} vehicle${rows.length > 1 ? 's' : ''}`);
            setSelectedTransactionId(null);
            setSearchQuery('');
            invalidateAfterAssignmentChange();
        },
        onError: (e: Error) => {
            captureError(e);
            toast.error(e.message || 'Failed to assign transaction');
        },
    });

    const unassignMutation = useMutation({
        mutationFn: async () => {
            if (!linkedTransactionId) return;

            // Link column FIRST (mirrors confirmMutation's ordering) — if the delete below fails,
            // the card already reads as unassigned and Confirm can retry cleanly instead of leaving
            // a dangling link with no rows behind it.
            const { error: boeErr } = await supabase
                .from('bills_of_entry')
                .update({ [BOE_LINK_COLUMNS[type]]: null })
                .eq('id', boe.id);
            if (boeErr) throw boeErr;

            const { error } = await supabase
                .from('vehicle_transactions')
                .delete()
                .eq('transaction_id', linkedTransactionId)
                .eq('transaction_type', type);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success(`${meta.label} unassigned`);
            setShowUnassignConfirm(false);
            invalidateAfterAssignmentChange();
        },
        onError: (e: Error) => {
            captureError(e);
            toast.error(e.message || 'Failed to unassign transaction');
        },
    });

    const updateRow = (vehicleId: number, field: 'amount' | 'fee', value: number) => {
        setRows(prev => prev.map(r => r.vehicleId === vehicleId ? { ...r, [field]: value } : r));
    };

    const eligibleSumAmount = rows.reduce((s, r) => s + r.amount, 0);
    const eligibleSumFee = rows.reduce((s, r) => s + r.fee, 0);
    // Differences between the allocated figures and the paid transaction are INFORMATIONAL, never
    // blocking (owner ruling 2026-07-21): BOE per-vehicle figures are kept as printed, and GRA's
    // document-level fees/levies legitimately make the payment exceed their sum.
    const amountDifference = selectedTransaction ? eligibleSumAmount - selectedTransaction.amount : 0;
    const feeDifference = selectedTransaction ? eligibleSumFee - (selectedTransaction.fee ?? 0) : 0;
    const hasDifference = Math.abs(amountDifference) > 0.01 || Math.abs(feeDifference) > 0.01;
    const showFeeRow = !!selectedTransaction && (selectedTransaction.fee ?? 0) !== 0;

    // ── Assigned state — show the linked transaction + each member's share, offer Unassign ──────
    if (linkedTransactionId) {
        // Only rows actually tied to THIS linked transaction count as "assigned" here — a member
        // can hold a same-type vehicle_transactions row under a DIFFERENT transaction_id (assigned
        // individually via VehicleTransactionAssigner, bypassing this shared BOE split); that row
        // must not be counted or displayed as part of this transaction's allocation.
        const assignedCount = allMembers.filter(m => vtByVehicleId.get(m.vehicleId)?.transaction_id === linkedTransactionId).length;
        // Same informational note as the pre-confirm view: per-vehicle figures are stored as
        // printed on the BOE, so their sum legitimately differs from the paid amount by the
        // document-level fees/levies.
        const assignedSum = allMembers.reduce((s, m) => {
            const vt = vtByVehicleId.get(m.vehicleId);
            return vt && vt.transaction_id === linkedTransactionId ? s + (vt.allocated_amount ?? 0) : s;
        }, 0);
        const assignedDifference = linkedTransactionSummary ? assignedSum - linkedTransactionSummary.amount : 0;
        return (
            <section className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <h4 className="font-medium">{meta.label}</h4>
                    <Badge variant="secondary" className="gap-1">
                        <Check className="h-3 w-3" /> Assigned
                    </Badge>
                </div>

                {linkedTransactionSummary && (
                    <div className="text-sm space-y-0.5">
                        <p className="font-medium">{formatMoney(linkedTransactionSummary.amount, linkedTransactionSummary.currency)}</p>
                        <p className="text-xs text-muted-foreground">
                            {formatBusinessDate(linkedTransactionSummary.transaction_at)}
                            {linkedTransactionSummary.description ? ` · ${linkedTransactionSummary.description}` : ''}
                        </p>
                    </div>
                )}

                <div className="rounded-md border overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Vehicle</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {allMembers.map(m => {
                                const vt = vtByVehicleId.get(m.vehicleId);
                                const isForeignRow = !!vt && vt.transaction_id !== linkedTransactionId;
                                return (
                                    <TableRow key={m.vehicleId} className={isForeignRow ? 'opacity-50' : undefined}>
                                        <TableCell className="text-sm">
                                            {m.label}
                                            {isForeignRow && <Badge variant="outline" className="ml-2 text-[10px]">Assigned individually</Badge>}
                                            <code className="block text-[10px] text-muted-foreground/60 font-mono">{m.vin}</code>
                                        </TableCell>
                                        <TableCell className="text-right text-sm">
                                            {isForeignRow
                                                ? '—'
                                                : vt ? formatMoney(vt.allocated_amount ?? 0, linkedTransactionSummary?.currency ?? 'GHS') : '—'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>

                {linkedTransactionSummary && Math.abs(assignedDifference) > 0.01 && (
                    <p className="text-xs text-amber-600">
                        Vehicle amounts sum to {formatMoney(assignedSum, linkedTransactionSummary.currency)} — differs from the
                        paid {formatMoney(linkedTransactionSummary.amount, linkedTransactionSummary.currency)} by {formatMoney(Math.abs(assignedDifference), linkedTransactionSummary.currency)};
                        per-vehicle figures are kept as printed on the Bill of Entry and the remainder is document-level fees/levies.
                    </p>
                )}

                <Button variant="outline" size="sm" className="text-destructive" onClick={() => setShowUnassignConfirm(true)}>
                    Unassign
                </Button>

                <AlertDialog open={showUnassignConfirm} onOpenChange={setShowUnassignConfirm}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Unassign {meta.label}?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This removes the transaction link from all {assignedCount} vehicle{assignedCount > 1 ? 's' : ''} it was assigned to. {assignedCount > 1 ? 'They' : 'It'} will revert to the prior pipeline stage and the transaction becomes unassigned again.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => unassignMutation.mutate()}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                Unassign
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </section>
        );
    }

    // ── Unassigned state — search, select, allocate, confirm ────────────────────────────────────
    return (
        <section className="rounded-lg border p-4 space-y-3">
            <h4 className="font-medium">{meta.label}</h4>

            {alreadyAssignedMembers.length > 0 && (
                <p className="text-xs text-muted-foreground">
                    {alreadyAssignedMembers.length} vehicle{alreadyAssignedMembers.length > 1 ? 's' : ''} already{' '}
                    {alreadyAssignedMembers.length > 1 ? 'have' : 'has'} a {meta.label.toLowerCase()} transaction assigned — excluded from the split below.
                </p>
            )}

            {!selectedTransactionId ? (
                <>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search GHS/USD debit transactions..."
                            className="pl-9"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                        {searchLoading ? (
                            <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>
                        ) : (searchResults?.length ?? 0) === 0 ? (
                            <p className="text-sm text-muted-foreground py-4 text-center">No unassigned debit transactions found</p>
                        ) : (
                            searchResults!.map(t => (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setSelectedTransactionId(t.id)}
                                    className="w-full text-left rounded-md border p-2.5 hover:border-primary hover:bg-primary/5 transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium text-sm">{formatMoney(t.amount, t.currency)}</span>
                                        <span className="text-xs text-muted-foreground">{formatBusinessDate(t.transaction_at)}</span>
                                    </div>
                                    {t.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{t.description}</p>}
                                </button>
                            ))
                        )}
                    </div>
                </>
            ) : !selectedTransaction ? (
                <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>
            ) : (
                <>
                    <div className="flex items-center justify-between rounded-md bg-muted/50 p-2.5">
                        <div>
                            <p className="font-medium text-sm">{formatMoney(selectedTransaction.amount, selectedTransaction.currency)}</p>
                            <p className="text-xs text-muted-foreground">
                                {formatBusinessDate(selectedTransaction.transaction_at)}
                                {selectedTransaction.description ? ` · ${selectedTransaction.description}` : ''}
                            </p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedTransactionId(null)}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="rounded-md border overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Vehicle</TableHead>
                                    <TableHead className="text-right">Amount</TableHead>
                                    {showFeeRow && <TableHead className="text-right">Fee</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {alreadyAssignedMembers.map(m => (
                                    <TableRow key={m.vehicleId} className="opacity-50">
                                        <TableCell className="text-sm">
                                            {m.label}
                                            <Badge variant="outline" className="ml-2 text-[10px]">Already assigned</Badge>
                                            <code className="block text-[10px] text-muted-foreground/60 font-mono">{m.vin}</code>
                                        </TableCell>
                                        <TableCell className="text-right text-sm">
                                            {formatMoney(vtByVehicleId.get(m.vehicleId)?.allocated_amount ?? 0, selectedTransaction.currency)}
                                        </TableCell>
                                        {showFeeRow && (
                                            <TableCell className="text-right text-sm">
                                                {formatMoney(vtByVehicleId.get(m.vehicleId)?.allocated_fee ?? 0, selectedTransaction.currency)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                {rows.map(r => (
                                    <TableRow key={r.vehicleId}>
                                        <TableCell className="text-sm">
                                            {r.label}
                                            <code className="block text-[10px] text-muted-foreground/60 font-mono">{r.vin}</code>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={r.amount}
                                                onChange={(e) => updateRow(r.vehicleId, 'amount', parseFloat(e.target.value) || 0)}
                                                className="h-8 text-right ml-auto w-28"
                                            />
                                        </TableCell>
                                        {showFeeRow && (
                                            <TableCell className="text-right">
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={r.fee}
                                                    onChange={(e) => updateRow(r.vehicleId, 'fee', parseFloat(e.target.value) || 0)}
                                                    className="h-8 text-right ml-auto w-24"
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="space-y-1 pt-1 text-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Allocated amount</span>
                            <span className={Math.abs(amountDifference) > 0.01 ? 'text-amber-600 font-medium' : 'text-emerald-600 font-medium'}>
                                {formatMoney(eligibleSumAmount, selectedTransaction.currency)} / {formatMoney(selectedTransaction.amount, selectedTransaction.currency)}
                            </span>
                        </div>
                        {showFeeRow && (
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Allocated fee</span>
                                <span className={Math.abs(feeDifference) > 0.01 ? 'text-amber-600 font-medium' : 'text-emerald-600 font-medium'}>
                                    {formatMoney(eligibleSumFee, selectedTransaction.currency)} / {formatMoney(selectedTransaction.fee ?? 0, selectedTransaction.currency)}
                                </span>
                            </div>
                        )}
                    </div>
                    {hasDifference && (
                        <p className="text-xs text-amber-600">
                            Differs from the paid amount by {formatMoney(Math.abs(amountDifference) > 0.01 ? Math.abs(amountDifference) : Math.abs(feeDifference), selectedTransaction.currency)} —
                            per-vehicle figures are kept as printed on the Bill of Entry; the remainder is document-level fees/levies and stays on the container transaction.
                        </p>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setSelectedTransactionId(null)}>Cancel</Button>
                        <Button size="sm" disabled={rows.length === 0 || confirmMutation.isPending} onClick={() => confirmMutation.mutate()}>
                            {confirmMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Confirm
                        </Button>
                    </div>
                </>
            )}
        </section>
    );
}
